import {
  CALIDADES_LEAD,
  FUENTES_LEAD,
  ORIGENES_PERSONA,
  RANGOS_ULTIMO_ENTRANTE,
  type FiltrosAudiencia,
  type OpcionesEmisionPublic,
} from '@app/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Alert } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { emisionApi } from '../api/emision.api';

import { GrupoOpciones } from './Piezas';

// ciclo-emisor, parte 2: "1. A quién". Un menú CERRADO de filtros (decisión de
// Javier, 19/10) y el contador en vivo: a cuántos les llegaría y por qué quedan
// afuera los demás. Lo cuenta el mismo código que fija la lista al activar.

const ETAPAS = [1, 2, 3, 4, 5] as const;
// Basura y Compró siempre quedan afuera: no tiene sentido ofrecerlos.
const CALIDADES = CALIDADES_LEAD.filter((c) => c === 'BUENO' || c === 'SIN_CALIFICAR');

function useDiferido<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function FiltrosAudienciaForm({
  filtros,
  onChange,
  opciones,
  bloqueado,
}: {
  filtros: FiltrosAudiencia;
  onChange: (f: FiltrosAudiencia) => void;
  opciones: OpcionesEmisionPublic | undefined;
  /** La lista ya está fija: se muestran, no se cambian. */
  bloqueado: boolean;
}): JSX.Element {
  const t = useI18n().campanasEnvio.filtros;
  const set = (parcial: Partial<FiltrosAudiencia>): void => {
    const nuevo = { ...filtros, ...parcial } as Record<string, unknown>;
    // Un filtro vacío se saca: "ninguno tildado" es "no filtra", no "no hay nadie".
    for (const [k, v] of Object.entries(nuevo)) {
      if (v === undefined || (Array.isArray(v) && v.length === 0 && k !== 'origenes')) delete nuevo[k];
    }
    onChange(nuevo as FiltrosAudiencia);
  };
  const paises = (opciones?.paises ?? []).slice(0, 12).map((p) => p.codigo);
  const campanas = ['NINGUNA', ...(opciones?.campanasOrigen ?? []).map((c) => c.id)];
  const nombreCampana = (id: string): string =>
    id === 'NINGUNA' ? t.ninguna : (opciones?.campanasOrigen.find((c) => c.id === id)?.nombre ?? id);

  return (
    <fieldset disabled={bloqueado} className="space-y-5">
      <Fila titulo={t.quienes}>
        <GrupoOpciones
          opciones={ORIGENES_PERSONA}
          elegidas={filtros.origenes}
          onChange={(origenes) => set({ origenes })}
          etiqueta={(o) => t.origenes[o]}
          deshabilitado={bloqueado}
        />
        {filtros.origenes.includes('SCRAPER') && (
          <p className="mt-2 flex items-start gap-2 rounded-md bg-marca-amarillo/15 p-2 text-xs text-text-strong">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            {t.avisoScraper}
          </p>
        )}
      </Fila>

      <Fila titulo={t.ultimoEntrante}>
        <GrupoOpciones
          opciones={RANGOS_ULTIMO_ENTRANTE}
          elegidas={filtros.ultimoEntrante ?? []}
          onChange={(ultimoEntrante) => set({ ultimoEntrante })}
          etiqueta={(r) => t.rangos[r]}
          deshabilitado={bloqueado}
        />
      </Fila>

      <Fila titulo={t.etapa}>
        <div className="flex items-center gap-2 text-sm text-text-subtle">
          {t.desde}
          <SelectorEtapa valor={filtros.etapaMin} onChange={(etapaMin) => set({ etapaMin })} />
          {t.hasta}
          <SelectorEtapa valor={filtros.etapaMax} onChange={(etapaMax) => set({ etapaMax })} />
        </div>
      </Fila>

      <Fila titulo={t.calidad}>
        <GrupoOpciones
          opciones={CALIDADES}
          elegidas={filtros.calidades ?? []}
          onChange={(calidades) => set({ calidades })}
          etiqueta={(c) => t.calidades[c]}
          deshabilitado={bloqueado}
        />
      </Fila>

      <Fila titulo={t.fuente}>
        <GrupoOpciones
          opciones={FUENTES_LEAD}
          elegidas={filtros.fuentes ?? []}
          onChange={(fuentes) => set({ fuentes })}
          etiqueta={(f) => t.fuentes[f]}
          deshabilitado={bloqueado}
        />
      </Fila>

      <Fila titulo={t.campanaOrigen}>
        <GrupoOpciones
          opciones={campanas}
          elegidas={filtros.campanasOrigen ?? []}
          onChange={(campanasOrigen) => set({ campanasOrigen })}
          etiqueta={nombreCampana}
          deshabilitado={bloqueado}
        />
      </Fila>

      <Fila titulo={t.pais}>
        <GrupoOpciones
          opciones={paises}
          elegidas={filtros.paises ?? []}
          onChange={(p) => set({ paises: p })}
          etiqueta={(p) => p}
          deshabilitado={bloqueado}
        />
      </Fila>

      <Fila titulo={t.descanso}>
        <label className="flex items-center gap-2 text-sm text-text-subtle">
          <input
            type="checkbox"
            checked={filtros.descansoDias !== undefined}
            onChange={(e) => set({ descansoDias: e.target.checked ? 30 : undefined })}
          />
          {t.descansoAyuda}
          <Input
            type="number"
            min={1}
            max={365}
            className="h-8 w-20"
            disabled={bloqueado || filtros.descansoDias === undefined}
            value={filtros.descansoDias ?? 30}
            onChange={(e) => set({ descansoDias: Math.max(1, Number(e.target.value) || 1) })}
          />
          {t.dias}
        </label>
      </Fila>

      <p className="text-xs text-text-faint">
        {t.cualquiera} {t.siempre}
      </p>
    </fieldset>
  );
}

function Fila({ titulo, children }: { titulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-[180px_1fr] items-start gap-4">
      <div className="pt-1.5 text-sm font-medium text-text-strong">{titulo}</div>
      <div>{children}</div>
    </div>
  );
}

function SelectorEtapa({
  valor,
  onChange,
}: {
  valor: number | undefined;
  onChange: (v: number | undefined) => void;
}): JSX.Element {
  return (
    <select
      value={valor ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
      className="h-9 rounded-md border border-marca-neutro bg-surface px-2 text-sm text-marca-gris"
    >
      <option value="">-</option>
      {ETAPAS.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  );
}

/** El contador en vivo: se pide medio segundo después del último cambio de filtros. */
export function ContadorAudiencia({ filtros }: { filtros: FiltrosAudiencia }): JSX.Element {
  const es = useI18n();
  const t = es.campanasEnvio.audiencia;
  const [verMuestra, setVerMuestra] = useState(false);
  const diferidos = useDiferido(filtros, 500);
  const valido = diferidos.origenes.length > 0;
  const { data, isFetching, error } = useQuery({
    queryKey: qk.campanasEnvio.audiencia(diferidos),
    queryFn: () => emisionApi.audiencia(diferidos),
    enabled: valido,
    placeholderData: (anterior) => anterior,
  });

  return (
    <div className="rounded-xl border border-marca-primario/30 bg-marca-primario/5 p-4">
      <div className="flex items-center gap-3">
        <Users size={22} className="text-marca-primario" />
        <span className="text-xl font-bold text-text-strong">
          {!valido
            ? '-'
            : data
              ? t.llegaria.replace('{n}', data.total.toLocaleString('es-AR'))
              : t.calculando}
        </span>
        {isFetching && <span className="text-xs text-text-faint">{t.calculando}</span>}
      </div>
      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}
      {data && Object.keys(data.excluidos).length > 0 && (
        <p className="mt-2 text-xs text-text-subtle">
          {t.excluidos}{' '}
          {Object.entries(data.excluidos)
            .map(([motivo, n]) => `${motivo} ${String(n)}`)
            .join(' · ')}
        </p>
      )}
      {data && data.total > 0 && (
        <button
          type="button"
          className="mt-2 text-xs font-medium text-marca-primario hover:underline"
          onClick={() => setVerMuestra((v) => !v)}
        >
          {verMuestra ? t.ocultarMuestra : t.verMuestra}
        </button>
      )}
      {verMuestra && data && (
        <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-text-subtle">
          {data.muestra.map((m, i) => (
            <li key={i}>
              {m.nombre ?? t.sinNombre} <span className="text-text-faint">· {m.pais ?? '-'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
